<template>
  <nav class="navbar">
    <div class="navbar-container">
      <!-- 左侧：Logo和导航链接 -->
      <div class="navbar-left">
        <router-link to="/" class="navbar-logo">
          <el-icon><Box /></el-icon>
          <span>3D模型库</span>
        </router-link>
        
        <div class="navbar-links">
          <router-link v-if="isAuthenticated" to="/models" class="nav-link">
            <el-icon><Collection /></el-icon>
            模型库
          </router-link>
          <router-link to="/models/upload" class="nav-link" v-if="isDesigner">
            <el-icon><Upload /></el-icon>
            上传模型
          </router-link>
          <a href="#" class="nav-link" v-if="isAdmin" @click.prevent="showAdminPanel">
            <el-icon><Setting /></el-icon>
            管理面板
          </a>
        </div>
      </div>
      
      <!-- 右侧：用户相关 -->
      <div class="navbar-right">
        <div class="user-info" v-if="isAuthenticated">
          <el-dropdown @command="handleCommand">
            <span class="user-dropdown">
              <el-avatar :size="32" :src="userAvatar" class="user-avatar">
                {{ userInitials }}
              </el-avatar>
              <span class="user-name">{{ currentUser.username }}</span>
              <el-icon class="dropdown-icon"><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">
                  <el-icon><User /></el-icon>
                  个人资料
                </el-dropdown-item>
                <el-dropdown-item command="points" v-if="currentUser.points !== undefined">
                  <el-icon><Coin /></el-icon>
                  积分: {{ currentUser.points }}
                </el-dropdown-item>
                <el-dropdown-item divided command="logout">
                  <el-icon><SwitchButton /></el-icon>
                  退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
        
        <div class="auth-buttons" v-else>
          <el-button :text="true" @click="$router.push('/login')" class="login-btn">
            登录
          </el-button>
          <el-button type="primary" @click="$router.push('/register')" size="small">
            注册
          </el-button>
        </div>

        <!-- 汉堡菜单按钮（仅移动端显示） -->
        <el-button class="mobile-menu-btn" text @click="toggleMobileMenu">
          <el-icon :size="24" v-if="!mobileMenuOpen"><Expand /></el-icon>
          <el-icon :size="24" v-else><Fold /></el-icon>
        </el-button>
      </div>
    </div>

    <!-- 移动端抽屉菜单 -->
    <transition name="mobile-menu-fade">
      <div v-if="mobileMenuOpen" class="mobile-menu-overlay" @click="closeMobileMenu">
        <div class="mobile-menu-drawer" @click.stop>
          <div class="mobile-menu-header">
            <router-link to="/" class="mobile-logo" @click="closeMobileMenu">
              <el-icon><Box /></el-icon>
              <span>3D模型库</span>
            </router-link>
            <el-button text @click="closeMobileMenu">
              <el-icon :size="22"><Fold /></el-icon>
            </el-button>
          </div>
          <div class="mobile-menu-items">
            <router-link
              to="/"
              class="mobile-nav-item"
              @click="closeMobileMenu"
            >
              <el-icon><HomeFilled /></el-icon>
              首页
            </router-link>
            <router-link
              v-if="isAuthenticated"
              to="/models"
              class="mobile-nav-item"
              @click="closeMobileMenu"
            >
              <el-icon><Collection /></el-icon>
              模型库
            </router-link>
            <router-link
              v-if="isDesigner"
              to="/models/upload"
              class="mobile-nav-item"
              @click="closeMobileMenu"
            >
              <el-icon><Upload /></el-icon>
              上传模型
            </router-link>
            <a
              href="#"
              v-if="isAdmin"
              class="mobile-nav-item"
              @click.prevent="navigateToAdmin"
            >
              <el-icon><Setting /></el-icon>
              管理面板
            </a>
            <div class="mobile-menu-divider" v-if="isAuthenticated"></div>
            <router-link
              v-if="isAuthenticated"
              to="/profile"
              class="mobile-nav-item"
              @click="closeMobileMenu"
            >
              <el-icon><User /></el-icon>
              个人资料
            </router-link>
            <a
              v-if="isAuthenticated"
              href="#"
              class="mobile-nav-item mobile-logout"
              @click.prevent="handleLogoutFromMenu"
            >
              <el-icon><SwitchButton /></el-icon>
              退出登录
            </a>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { ElMessageBox, ElMessage } from 'element-plus'
import {
  Box,
  Collection,
  Upload,
  Setting,
  ArrowDown,
  User,
  Coin,
  SwitchButton,
  Expand,
  Fold,
  HomeFilled
} from '@element-plus/icons-vue'

const store = useStore()
const router = useRouter()

// 汉堡菜单状态
const mobileMenuOpen = ref(false)

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}

// 计算属性
const isAuthenticated = computed(() => store.getters.isAuthenticated)
const isDesigner = computed(() => store.getters.isDesigner)
const isAdmin = computed(() => store.getters.isAdmin)
const currentUser = computed(() => store.getters.currentUser)

// 用户头像和 initials
const userAvatar = computed(() => {
  // 这里可以根据用户信息生成头像URL
  return ''
})

const userInitials = computed(() => {
  if (currentUser.value.username) {
    return currentUser.value.username.charAt(0).toUpperCase()
  }
  return 'U'
})

// 处理下拉菜单命令
const handleCommand = async (command) => {
  switch (command) {
    case 'profile':
      // 跳转到个人资料页面
      router.push('/profile')
      break
    case 'logout':
      await handleLogout()
      break
    // 其他命令...
  }
}

// 显示管理面板
const showAdminPanel = () => {
  mobileMenuOpen.value = false
  router.push('/admin')
}

// 移动端管理面板（关闭菜单后跳转）
const navigateToAdmin = () => {
  mobileMenuOpen.value = false
  router.push('/admin')
}

// 处理移动端退出登录
const handleLogoutFromMenu = async () => {
  mobileMenuOpen.value = false
  await handleLogout()
}

// 处理退出登录
const handleLogout = async () => {
  try {
    await ElMessageBox.confirm(
      '确定要退出登录吗？',
      '退出确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    // 调用store的logout action
    store.dispatch('logout')
    
    // 显示成功消息
    ElMessage.success('已成功退出登录')
    
    // 跳转到首页
    router.push('/')
    
  } catch (error) {
    // 用户取消了退出
    if (error !== 'cancel') {
      ElMessage.error('退出登录失败')
    }
  }
}
</script>

<style scoped>
.navbar {
  background: white;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.navbar-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 40px;
}

.navbar-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: #303133;
  font-size: 20px;
  font-weight: 600;
  
  .el-icon {
    font-size: 24px;
    color: #409eff;
  }
  
  &:hover {
    color: #409eff;
  }
}

.navbar-links {
  display: flex;
  align-items: center;
  gap: 24px;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: #606266;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.3s;
  
  .el-icon {
    font-size: 16px;
  }
  
  &:hover {
    color: #409eff;
    background: #f5f7fa;
  }
  
  &.router-link-active {
    color: #409eff;
    background: #ecf5ff;
  }
}

.navbar-right {
  display: flex;
  align-items: center;
}

.user-info {
  .user-dropdown {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 6px;
    transition: background 0.3s;
    
    &:hover {
      background: #f5f7fa;
    }
  }
  
  .user-avatar {
    background: #409eff;
    color: white;
    font-weight: 600;
  }
  
  .user-name {
    font-size: 14px;
    color: #303133;
    font-weight: 500;
  }
  
  .dropdown-icon {
    font-size: 12px;
    color: #909399;
  }
}

.auth-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
  
  .login-btn {
    color: #606266;
    
    &:hover {
      color: #409eff;
    }
  }
}

/* 汉堡菜单按钮 - 桌面隐藏 */
.mobile-menu-btn {
  display: none;
  margin-left: 8px;
}

/* 移动端菜单遮罩 */
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
}

.mobile-menu-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 280px;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.mobile-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
}

.mobile-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.mobile-logo .el-icon {
  font-size: 22px;
  color: #409eff;
}

.mobile-menu-items {
  flex: 1;
  padding: 12px 0;
  display: flex;
  flex-direction: column;
}

.mobile-nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  text-decoration: none;
  color: #303133;
  font-size: 15px;
  transition: background 0.2s;
}

.mobile-nav-item:hover {
  background: #f5f7fa;
  color: #409eff;
}

.mobile-nav-item .el-icon {
  font-size: 18px;
  color: #909399;
}

.mobile-nav-item:hover .el-icon {
  color: #409eff;
}

.mobile-nav-item.mobile-logout {
  color: #f56c6c;
  margin-top: auto;
  border-top: 1px solid #f0f0f0;
  padding-top: 16px;
}

.mobile-nav-item.mobile-logout .el-icon {
  color: #f56c6c;
}

.mobile-menu-divider {
  height: 1px;
  background: #f0f0f0;
  margin: 8px 16px;
}

/* 移动端菜单过渡动画 */
.mobile-menu-fade-enter-active,
.mobile-menu-fade-leave-active {
  transition: opacity 0.3s ease;
}

.mobile-menu-fade-enter-active .mobile-menu-drawer,
.mobile-menu-fade-leave-active .mobile-menu-drawer {
  transition: transform 0.3s ease;
}

.mobile-menu-fade-enter-from,
.mobile-menu-fade-leave-to {
  opacity: 0;
}

.mobile-menu-fade-enter-from .mobile-menu-drawer,
.mobile-menu-fade-leave-to .mobile-menu-drawer {
  transform: translateX(100%);
}

.mobile-menu-fade-enter-to .mobile-menu-drawer,
.mobile-menu-fade-leave-from .mobile-menu-drawer {
  transform: translateX(0);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .navbar-container {
    padding: 0 12px;
    height: 56px;
  }
  
  .navbar-links {
    display: none;
  }
  
  .nav-link {
    display: none;
  }
  
  .user-name {
    display: none;
  }

  .user-info .user-dropdown {
    padding: 4px 8px;
  }

  .mobile-menu-btn {
    display: flex;
  }

  .navbar-logo span {
    font-size: 17px;
  }

  .auth-buttons {
    gap: 8px;
  }

  .auth-buttons .login-btn {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .navbar-container {
    padding: 0 10px;
    height: 50px;
  }

  .navbar-logo span {
    font-size: 15px;
  }

  .mobile-menu-drawer {
    width: 260px;
  }
}
</style>
